import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectContext, selectContextStatus, getNamespacePath,
} from '../redux/features/context/contextSlice';
import {
  createTag,
  fetchTags,
  mergeTags,
  renameTag,
  selectAllTagsPrivate,
  selectAllTagsPublic,
  selectAllTagsPending,
} from '../redux/features/tags/tagsSlice';
import { HeaderSpacer } from '../components/Header';
import StyledBackdrop from '../components/StyledBackdrop';
import Login from '../components/Login';
import TagChip from '../components/TagChip';
import TagComposer, { TagDraftChip } from '../components/TagComposer';
import { BORDER, DRAFT_TEXT, MUTED, SANS_STACK, TEXT_DARK } from '../components/common/tokens';
import { FooterText, formatCount } from '../components/common/grid/CardGrid';

// How long a chip stays lit after the composer touches it.
const PULSE_DURATION = 950; // ms — matches the success pulse keyframes
const FLASH_DURATION = 1400; // ms — long enough to find the chip that already owns the name

function PlusIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ flexShrink: 0 }}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PageHeader({ isPublicView, canCreate, onNewTag }) {
  const theme = useTheme();
  const brandRed = theme.palette.primary.main;
  return (
    <Box sx={{
      marginBottom: '20px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '12px',
    }}>
      <Box>
        <Box component="h1" sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          color: TEXT_DARK,
          fontFamily: SANS_STACK,
          margin: '0 0 4px',
        }}>
          Tags
        </Box>
        {!isPublicView && (
          <Box component="p" sx={{
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            margin: 0,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Hold any tag to rename it inline.
          </Box>
        )}
      </Box>
      {canCreate && (
        <Box
          component="button"
          type="button"
          onClick={onNewTag}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            border: `1px solid ${brandRed}`,
            borderRadius: '6px',
            background: 'transparent',
            color: brandRed,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: SANS_STACK,
            padding: '6px 12px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <PlusIcon size={14} />
          New tag
        </Box>
      )}
    </Box>
  );
}

/**
 * Shown when the namespace has no tags at all — the grid has nothing to explain
 * itself with, so the empty state carries the pitch and the create trigger.
 */
function TagsEmptyState({ isPublicView, canCreate, onNewTag }) {
  const theme = useTheme();
  const brandRed = theme.palette.primary.main;
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: '48px 16px',
      color: DRAFT_TEXT,
    }}>
      <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke={BORDER} strokeWidth="1.5" strokeDasharray="3 2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
      <Box sx={{
        fontSize: 15, fontWeight: 700, color: TEXT_DARK, margin: '14px 0 6px',
      }}>
        No tags yet
      </Box>
      <Box sx={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 380 }}>
        {isPublicView
          ? 'Public tags are the shared vocabulary across all users.'
          : 'Your own tags for items in your library. Add one here, or add tags straight from an item card.'}
      </Box>
      {canCreate && (
        <Box
          component="button"
          type="button"
          onClick={onNewTag}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            border: 'none',
            borderRadius: '6px',
            background: brandRed,
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: SANS_STACK,
            padding: '9px 18px',
            marginTop: '18px',
            cursor: 'pointer',
          }}
        >
          <PlusIcon size={15} />
          New tag
        </Box>
      )}
      <Box sx={{ fontSize: 11, color: MUTED, marginTop: '14px' }}>
        {isPublicView
          ? 'Public tags are visible to everyone browsing.'
          : 'Tags stay private to your library.'}
      </Box>
    </Box>
  );
}

function TagsCountFooter({ count }) {
  const countLabel = formatCount(count, 'tag', 'tags');
  if (!countLabel) return null;
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      padding: 1,
      paddingRight: '16px',
      minHeight: 40,
      flexShrink: 0,
    }}>
      <FooterText>{countLabel}</FooterText>
    </Box>
  );
}

function TagsView(props) {
  const type = 'tags';
  const { namespace } = props;
  const isPublicView = namespace === 'public';
  const isPendingView = namespace === 'pending';
  // Public and pending are both read-only; only the private library is editable.
  const editable = !(isPublicView || isPendingView);
  const { userInfo } = useSelector((state) => state.users);

  // Adding to the public vocabulary is moderators only. Nobody creates pending
  // tags directly - they are created via shared items.
  let canCreate = !!userInfo;
  if (isPendingView) canCreate = false;
  else if (isPublicView) canCreate = !!userInfo?.isModerator;

  const status = useSelector(selectContextStatus);
  const currContext = useSelector(selectContext);
  const context = (currContext
    && currContext.namespace === namespace
    && currContext.type === type) ? currContext : null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const namespacePath = getNamespacePath(namespace);

  useEffect(() => {
    if (!isPublicView && !userInfo) return;
    if (status === 'loading') return;
    dispatch(fetchTags({ namespace, type }));
  }, [namespace, isPublicView, userInfo]);

  let tagsSelector = selectAllTagsPrivate;
  if (isPendingView) tagsSelector = selectAllTagsPending;
  else if (isPublicView) tagsSelector = selectAllTagsPublic;
  const tags = useSelector(tagsSelector);
  const tagsStatus = useSelector((state) => state.tags.status);

  // Per-tag set of *other* tag names — used for collision detection.
  // Memoized as a Map so each chip gets a stable reference (good for React.memo).
  const otherTagNamesById = useMemo(() => {
    const all = tags.map((t) => t.name);
    const map = new Map();
    tags.forEach((t, i) => {
      const others = new Set();
      for (let j = 0; j < all.length; j += 1) if (j !== i) others.add(all[j]);
      map.set(t.id, others);
    });
    return map;
  }, [tags]);

  const handleRename = useCallback((tagId, name) => {
    dispatch(renameTag({ tagId, name }));
  }, [dispatch]);

  const handleMerge = useCallback((sourceTagId, targetName) => {
    const target = tags.find((t) => t.name === targetName);
    if (!target) return;
    dispatch(mergeTags({ targetTagId: target.id, sourceTagIds: [sourceTagId] }));
  }, [dispatch, tags]);

  const handleOpen = useCallback((tagId) => {
    navigate(`/${namespacePath}/items?tag=${tagId}`);
  }, [navigate, namespacePath]);

  // Synthetic pseudo-tag (private only). Stable reference so the memoized chip doesn't re-render.
  const untaggedTag = useMemo(() => ({ id: 'untagged', name: 'untagged' }), []);

  const [composerOpen, setComposerOpen] = useState(false);
  // Creates still in flight, or failed and waiting on a retry. They hold the
  // spot the real chip will take so the grid doesn't reshuffle on arrival.
  const [drafts, setDrafts] = useState([]);
  // Tags created in this session, newest first — kept at the head of the grid.
  const [recentIds, setRecentIds] = useState([]);
  const [pulseIds, setPulseIds] = useState([]);
  const [flashTagId, setFlashTagId] = useState(null);

  const draftSeq = useRef(0);
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Switching namespace swaps the whole vocabulary out.
  useEffect(() => {
    setComposerOpen(false);
    setDrafts([]);
    setRecentIds([]);
    setPulseIds([]);
    setFlashTagId(null);
  }, [namespace]);

  // Names the composer must refuse, drafts included.
  const existingNames = useMemo(() => {
    const names = new Set(tags.map((t) => (t.name || '').toLowerCase()));
    drafts.forEach((d) => names.add(d.name));
    return names;
  }, [tags, drafts]);

  const runCreate = useCallback((key, name) => {
    dispatch(createTag({ tag: { name }, namespace })).unwrap()
      .then((tag) => {
        setDrafts((d) => d.filter((x) => x.key !== key));
        setRecentIds((ids) => [tag.id, ...ids.filter((id) => id !== tag.id)]);
        setPulseIds((ids) => [...ids, tag.id]);
        timers.current.push(setTimeout(
          () => setPulseIds((ids) => ids.filter((id) => id !== tag.id)),
          PULSE_DURATION,
        ));
      })
      .catch(() => {
        setDrafts((d) => d.map((x) => (x.key === key ? { ...x, status: 'error' } : x)));
      });
  }, [dispatch, namespace]);

  const handleCreate = useCallback((name) => {
    draftSeq.current += 1;
    const key = `draft-${draftSeq.current}`;
    setDrafts((d) => [...d, { key, name, status: 'pending' }]);
    runCreate(key, name);
  }, [runCreate]);

  const handleRetryDraft = useCallback((key) => {
    const draft = drafts.find((d) => d.key === key);
    if (!draft) return;
    setDrafts((d) => d.map((x) => (x.key === key ? { ...x, status: 'pending' } : x)));
    runCreate(key, draft.name);
  }, [drafts, runCreate]);

  // The composer only knows the name it refused; point at the chip that owns it.
  const handleDuplicate = useCallback((name) => {
    const existing = tags.find((t) => (t.name || '').toLowerCase() === name);
    if (!existing) return;
    setFlashTagId(existing.id);
    timers.current.push(setTimeout(() => setFlashTagId(null), FLASH_DURATION));
  }, [tags]);

  const handleOpenComposer = useCallback(() => setComposerOpen(true), []);
  const handleCloseComposer = useCallback(() => setComposerOpen(false), []);

  const orderedTags = useMemo(() => {
    if (!recentIds.length) return tags;
    const byId = new Map(tags.map((t) => [t.id, t]));
    const recent = recentIds.map((id) => byId.get(id)).filter(Boolean);
    const recentIdSet = new Set(recent.map((t) => t.id));
    return [...recent, ...tags.filter((t) => !recentIdSet.has(t.id))];
  }, [tags, recentIds]);

  // Only once the fetch has settled — an empty store mid-load isn't an empty
  // vocabulary, and "No tags yet" shouldn't flash on the way in.
  const showEmptyState = tagsStatus === 'succeeded'
    && tags.length === 0 && drafts.length === 0 && !composerOpen;

  return (
    <>
      <HeaderSpacer />
      {!isPublicView && !userInfo && <Login />}
      {(isPublicView || userInfo) && (
        <StyledBackdrop open={!context && status === 'loading'}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      {(isPublicView || userInfo) && (
        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 20px 0',
        }}
        >
          <PageHeader
            isPublicView={isPublicView}
            canCreate={canCreate}
            onNewTag={handleOpenComposer}
          />

          <Box sx={{
            flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: '12px',
          }}>
            {showEmptyState ? (
              <TagsEmptyState
                isPublicView={isPublicView}
                canCreate={canCreate}
                onNewTag={handleOpenComposer}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px 10px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}>
                {canCreate && composerOpen && (
                  <TagComposer
                    existingNames={existingNames}
                    onCreate={handleCreate}
                    onDuplicate={handleDuplicate}
                    onClose={handleCloseComposer}
                  />
                )}
                {editable && (
                  <TagChip
                    key="untagged"
                    tag={untaggedTag}
                    synthetic
                    onOpen={handleOpen}
                  />
                )}
                {drafts.map((draft) => (
                  <TagDraftChip key={draft.key} draft={draft} onRetry={handleRetryDraft} />
                ))}
                {orderedTags.map((tag) => {
                  let highlight;
                  if (flashTagId === tag.id) highlight = 'duplicate';
                  else if (pulseIds.includes(tag.id)) highlight = 'created';
                  return (
                    <TagChip
                      key={tag.id}
                      tag={tag}
                      highlight={highlight}
                      otherTagNames={editable ? otherTagNamesById.get(tag.id) : undefined}
                      onRename={editable ? handleRename : undefined}
                      onMerge={editable ? handleMerge : undefined}
                      onOpen={handleOpen}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <TagsCountFooter count={tags.length} />
        </Box>
      )}
    </>
  );
}

export default TagsView;
